import React, { useMemo, useState } from "react";
import { ArrowRightIcon, CheckIcon, DollarIcon, SettingsIcon, UsersIcon } from "../Icons";
import { Pricing, money } from "../business-case/calculator";
import { useLeadForm } from "../lead-form/LeadFormProvider";
import { FIELD_CONTROL, FIELD_LABEL, IconTile, Notice, PrimaryButton, Section, SectionHeading, SectionLabel, SelectField, TileVariant, readNumberField } from "../ui";
import { addOnIcon } from "./content";
import { ON_REQUEST, PlanChoice, buildEstimate, durationFor, initialEstimatorState, resolveSupportLevel } from "./estimate";

const STEP_HEADINGS: {
  title: string;
  hint: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  variant?: TileVariant;
}[] = [
  {
    title: "How would you like to start?",
    hint: "Choose a pilot or annual programme.",
    icon: DollarIcon,
    variant: "solid",
  },
  {
    title: "Programme size",
    hint: "How many people will participate?",
    icon: UsersIcon,
    variant: "solid",
  },
  { title: "Level of support", hint: "Choose a starting point.", icon: SettingsIcon, variant: "dark" },
  { title: "Customise your support", hint: "Select only the services you need." },
];

const PILOT_CAP_NOTICE_ID = "pricing-pilot-cap";

// BusinessCase::Pricing names the middle level "supported"; LeadRequest calls the same thing
// "launch". Anything unrecognised falls through to the question rather than guessing an answer.
const SUPPORT_LEVEL_TO_ENQUIRY: Record<string, string> = {
  platform: "platform",
  supported: "launch",
  managed: "managed",
};

const EXTERNAL_MENTORS_ADD_ON = "external_mentors";

// Read from what they built rather than asked again: the add-on says they want FutureLab's network,
// the mentor count says they have their own. Neither signal means neither answer, so leave it blank.
function mentorPoolFrom(addOns: string[], mentors: number): string | undefined {
  const external = addOns.includes(EXTERNAL_MENTORS_ADD_ON);
  const internal = mentors > 0;

  if (external && internal) return "mixed";
  if (external) return "external";
  if (internal) return "internal";

  return undefined;
}

function StepBlock({ index, children }: { index: number; children: React.ReactNode }) {
  const { title, hint, icon, variant } = STEP_HEADINGS[index];

  return (
    <div className="tw-border-t tw-border-[#dce8f2] tw-pt-8 first:tw-border-t-0 first:tw-pt-0">
      <div className="tw-flex tw-items-start tw-gap-3">
        {icon ? <IconTile icon={icon} variant={variant} /> : null}
        <div className="tw-min-w-0">
          <p className="tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.12em] tw-text-[#087bd3]">
            {index + 1}. {title}
          </p>
          <p className="tw-mt-2 tw-mb-0 tw-text-sm tw-text-[#5b7188]">{hint}</p>
        </div>
      </div>
      <div className="tw-mt-5">{children}</div>
    </div>
  );
}

// Unavailable options stay focusable rather than `disabled`, so the reason they carry in
// `describedBy` is reachable by keyboard and screen reader instead of being skipped over.
function OptionCard({
  selected,
  unavailable = false,
  describedBy,
  onSelect,
  eyebrow,
  headline,
  body,
}: {
  selected: boolean;
  unavailable?: boolean;
  describedBy?: string;
  onSelect: () => void;
  eyebrow: string;
  headline: string;
  body: string;
}) {
  const surface = unavailable
    ? "tw-cursor-not-allowed tw-border-[#dfe9f1] tw-bg-[#f4f7fa]"
    : selected
      ? "tw-border-[#087bd3] tw-bg-[#e9f4fd]"
      : "tw-border-[#c9deed] tw-bg-white hover:tw-bg-[#f4f9fd]";

  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-disabled={unavailable || undefined}
      aria-describedby={describedBy}
      onClick={() => {
        if (!unavailable) onSelect();
      }}
      className={`tw-flex tw-h-full tw-flex-col tw-items-start tw-gap-1 tw-rounded-2xl tw-border tw-p-5 tw-text-left tw-transition-colors focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-[#087bd3] ${surface}`}
    >
      <span
        className={`tw-text-xs tw-font-black tw-uppercase tw-tracking-[.12em] ${
          unavailable ? "tw-text-[#8a9db0]" : "tw-text-[#5b7188]"
        }`}
      >
        {eyebrow}
      </span>
      <span className={`tw-text-lg tw-font-black ${unavailable ? "tw-text-[#6c8199]" : "tw-text-[#173754]"}`}>
        {headline}
      </span>
      <span className={`tw-text-sm tw-leading-6 ${unavailable ? "tw-text-[#7f95aa]" : "tw-text-[#5b7188]"}`}>
        {body}
      </span>
    </button>
  );
}

export default function Estimator({ pricing }: { pricing: Pricing }) {
  const [state, setState] = useState(() => initialEstimatorState(pricing));
  const openLeadForm = useLeadForm();

  const estimate = useMemo(() => buildEstimate(state, pricing), [state, pricing]);
  const support = useMemo(() => resolveSupportLevel(state, pricing), [state, pricing]);

  const supportName = pricing.supportLevels.find((level) => level.key === support.key)?.name;

  const setPlan = (plan: PlanChoice) => setState((current) => ({ ...current, plan }));

  const setCount = (key: "mentees" | "mentors", value: number) =>
    setState((current) => ({ ...current, [key]: Number.isFinite(value) ? Math.max(0, value) : 0 }));

  const applyLevel = (key: string, addOns: string[]) =>
    setState((current) => ({ ...current, level: key, addOns: [...addOns] }));

  const setDuration = (key: string, months: number) =>
    setState((current) => ({ ...current, durations: { ...current.durations, [key]: months } }));

  const toggleAddOn = (key: string) =>
    setState((current) => ({
      ...current,
      addOns: current.addOns.includes(key)
        ? current.addOns.filter((entry) => entry !== key)
        : [...current.addOns, key],
    }));

  const annualFrom = (pricing.annualTiers[0].annualPrice ?? 0) + pricing.setupFee;

  // The pilot is one fixed cohort, so above its cap it stops being an option rather than a default
  // the estimator quietly overrides.
  const overPilotCap = estimate.participants > pricing.pilot.maxParticipants;

  return (
    <Section id="estimate" className="tw-scroll-mt-24 tw-bg-[#eef7fd]">
      <div className="tw-mx-auto tw-max-w-3xl tw-text-center">
        <SectionLabel>Instant programme estimate</SectionLabel>
        <SectionHeading>Build the programme that fits your team.</SectionHeading>
        <p className="tw-mt-5 tw-mb-0 tw-text-lg tw-leading-8 tw-text-[#5b7188]">
          Select your programme size and the support you need. Your indicative first-year estimate
          updates instantly.
        </p>
      </div>

      <div className="tw-mt-12 tw-grid tw-grid-cols-[1fr_380px] tw-items-start tw-gap-6 sm:tw-grid-cols-1">
        <div className="tw-grid tw-min-w-0 tw-gap-8 tw-rounded-[28px] tw-border tw-border-[#c9deed] tw-bg-white tw-p-8 sm:tw-p-6">
          <StepBlock index={0}>
            <div className="tw-grid tw-grid-cols-2 tw-gap-4 xs:tw-grid-cols-1">
              <OptionCard
                selected={estimate.plan === "pilot"}
                unavailable={overPilotCap}
                describedBy={overPilotCap ? PILOT_CAP_NOTICE_ID : undefined}
                onSelect={() => setPlan("pilot")}
                eyebrow="Six-month pilot"
                headline={money(pricing.pilot.price)}
                body={pricing.pilot.summary}
              />
              <OptionCard
                selected={estimate.plan === "annual"}
                onSelect={() => setPlan("annual")}
                eyebrow="Twelve-month programme"
                headline={`From ${money(annualFrom)}`}
                body="For organisations ready to run a larger programme or multiple cohorts."
              />
            </div>
          </StepBlock>

          <StepBlock index={1}>
            <div className="tw-grid tw-grid-cols-2 tw-gap-5 xs:tw-grid-cols-1">
              {(["mentees", "mentors"] as const).map((key) => (
                <div key={key} className="tw-min-w-0">
                  <label htmlFor={`pricing-${key}`} className={FIELD_LABEL}>
                    {key === "mentees" ? "Mentees" : "Mentors"}
                  </label>
                  <input
                    id={`pricing-${key}`}
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={100000}
                    step={1}
                    value={state[key]}
                    onChange={(event) => setCount(key, readNumberField(event))}
                    className={FIELD_CONTROL}
                  />
                </div>
              ))}
            </div>
            <p className="tw-mt-3 tw-mb-0 tw-text-xs tw-leading-5 tw-text-[#5b7188]">
              Mentors and mentees count toward the participant allowance. Administrator accounts are
              excluded.
            </p>
            {overPilotCap ? (
              <Notice id={PILOT_CAP_NOTICE_ID} className="tw-mt-4">
                This cohort exceeds the {pricing.pilot.maxParticipants}-person pilot limit, so the
                estimate uses twelve-month programme pricing.
              </Notice>
            ) : null}
          </StepBlock>

          <StepBlock index={2}>
            <div className="tw-grid tw-grid-cols-3 tw-gap-4 sm:tw-grid-cols-1">
              {pricing.supportLevels.map((level) => (
                <OptionCard
                  key={level.key}
                  selected={support.key === level.key}
                  onSelect={() => applyLevel(level.key, level.addOns)}
                  eyebrow={level.name}
                  headline={level.tagline}
                  body={level.description}
                />
              ))}
            </div>
            {support.customised && supportName ? (
              <p role="status" className="tw-mt-4 tw-mb-0 tw-text-sm tw-leading-6 tw-text-[#5b7188]">
                Customised from {supportName}.
              </p>
            ) : null}
          </StepBlock>

          <StepBlock index={3}>
            <div className="tw-grid tw-gap-3">
              {pricing.addOns.map((addOn) => {
                const id = `pricing-addon-${addOn.key}`;
                const Icon = addOnIcon(addOn.key);
                const checked = state.addOns.includes(addOn.key);
                const duration = durationFor(addOn, state.durations[addOn.key]);

                return (
                  // A div, not a label: the duration select must not sit inside the checkbox's label.
                  <div
                    key={addOn.key}
                    className={`tw-rounded-2xl tw-border tw-p-4 tw-transition-colors focus-within:tw-outline focus-within:tw-outline-2 focus-within:tw-outline-offset-2 focus-within:tw-outline-[#087bd3] ${
                      checked
                        ? "tw-border-[#087bd3] tw-bg-[#f4f9fd]"
                        : "tw-border-[#c9deed] tw-bg-white hover:tw-border-[#79b8e3]"
                    }`}
                  >
                    <div className="tw-flex tw-items-start tw-gap-3">
                      <input
                        id={id}
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAddOn(addOn.key)}
                        className="tw-mt-1 tw-h-4 tw-w-4 tw-shrink-0 tw-accent-[#087bd3]"
                      />
                      <Icon aria-hidden className="tw-mt-0.5 tw-h-5 tw-w-5 tw-shrink-0 tw-text-[#087bd3]" />
                      <label htmlFor={id} className="tw-m-0 tw-min-w-0 tw-cursor-pointer tw-font-normal">
                        <span className="tw-flex tw-flex-wrap tw-items-baseline tw-gap-x-3">
                          <span className="tw-font-black tw-text-[#173754]">{addOn.name}</span>
                          <span className="tw-text-sm tw-font-bold tw-text-[#087bd3]">
                            {addOn.priceLabel}
                          </span>
                        </span>
                        <span className="tw-mt-1 tw-block tw-text-sm tw-leading-6 tw-text-[#5b7188]">
                          {addOn.description}
                        </span>
                      </label>
                    </div>

                    {checked && addOn.durations && duration ? (
                      <div className="tw-mt-4 tw-border-t tw-border-[#cfe3f1] tw-pl-10 tw-pt-4">
                        <SelectField
                          id={`${id}-months`}
                          label="How long do you need our support?"
                          className="tw-max-w-[280px]"
                          value={String(duration.months)}
                          onChange={(value) => setDuration(addOn.key, Number(value))}
                          options={addOn.durations.map((option) => ({
                            value: String(option.months),
                            label: option.label,
                          }))}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </StepBlock>
        </div>

        <div className="tw-sticky tw-top-24 tw-min-w-0 tw-rounded-[28px] tw-bg-[#10233f] tw-p-7 tw-text-white sm:tw-static sm:tw-p-6">
          <div className="tw-flex tw-items-center tw-gap-3">
            <IconTile icon={DollarIcon} variant="mint" />
            <div className="tw-min-w-0">
              <p className="tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.12em] tw-text-[#70d9ce]">
                Your estimate
              </p>
              <p className="tw-mt-1 tw-mb-0 tw-text-sm tw-text-[#c2d3e2]">
                {estimate.participants} participants · {estimate.months} months
              </p>
            </div>
          </div>

          <p className="tw-mt-6 tw-mb-0 tw-text-xs tw-font-bold tw-uppercase tw-tracking-[.1em] tw-text-[#9db3c8]">
            {estimate.planCustom
              ? "Indicative estimate"
              : estimate.plan === "pilot"
                ? "Indicative pilot total"
                : "Indicative first-year total"}
          </p>
          <p aria-live="polite" className="tw-mt-1 tw-mb-0 tw-text-4xl tw-font-black tw-tracking-[-.04em]">
            {estimate.planCustom ? "Custom estimate" : money(estimate.knownTotal)}
          </p>

          <ul className="tw-mt-6 tw-mb-0 tw-list-none tw-space-y-3 tw-border-t tw-border-white/15 tw-p-0 tw-pt-5">
            {estimate.lines.map((line) => (
              <li key={line.key} className="tw-flex tw-justify-between tw-gap-4 tw-text-sm">
                <span className="tw-flex tw-min-w-0 tw-gap-2 tw-leading-6 tw-text-[#c2d3e2]">
                  <CheckIcon aria-hidden className="tw-mt-1 tw-h-4 tw-w-4 tw-shrink-0 tw-text-[#70d9ce]" />
                  {line.label}
                </span>
                <span className="tw-shrink-0 tw-font-black">
                  {line.amount === null ? ON_REQUEST : money(line.amount)}
                </span>
              </li>
            ))}
          </ul>

          {/* Naming external mentors below holds while it is the only service without a price,
              which spec/models/business_case/pricing_spec.rb asserts. */}
          {estimate.hasCustom ? (
            <Notice tone="inverse" className="tw-mt-5">
              {estimate.planCustom
                ? `A cohort this size is scoped individually, so there is no indicative total yet. The services priced above come to ${money(estimate.knownTotal)}.`
                : "External mentor costs are quoted on request. FutureLab works these out with you and confirms them in your formal quotation."}
            </Notice>
          ) : null}

          <PrimaryButton
            type="button"
            size="lg"
            variant="inverse"
            onClick={() =>
              openLeadForm({
                enquiryType: "quotation",
                prefill: {
                  participants: String(estimate.participants),
                  starting_option: estimate.plan,
                  support_level: SUPPORT_LEVEL_TO_ENQUIRY[support.key],
                  mentor_pool: mentorPoolFrom(state.addOns, state.mentors),
                },
                // Only what the priced lines below do not already say: the starting option is the
                // first of those lines, and reaches sales again as a form answer.
                estimateSummary: [
                  { label: "Programme size", value: `${state.mentees} mentees + ${state.mentors} mentors` },
                  {
                    label: "Support level",
                    value: support.customised ? `${supportName} (customised)` : `${supportName}`,
                  },
                  // The estimate card's own lines, so the services the visitor ticked reach sales
                  // instead of a total they have to ask about.
                  ...estimate.lines.map((line) => ({
                    label: line.label,
                    value: line.amount === null ? ON_REQUEST : money(line.amount),
                  })),
                  {
                    label: "Indicative estimate",
                    value: estimate.planCustom ? "Custom estimate" : money(estimate.knownTotal),
                  },
                ],
              })
            }
            className="tw-mt-6 tw-box-border tw-w-full"
          >
            Request a formal quotation
            <ArrowRightIcon aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0" />
          </PrimaryButton>

          <p className="tw-mt-5 tw-mb-0 tw-text-xs tw-leading-5 tw-text-[#9db3c8]">
            Indicative planning estimate only. The pilot includes standard setup and administrator
            onboarding. Prices exclude applicable taxes, travel, custom integrations and external
            mentor fees. Final scope and pricing are confirmed in a formal quotation.
          </p>

          <p className="tw-mt-4 tw-mb-0 tw-flex tw-items-start tw-gap-2 tw-text-xs tw-leading-5 tw-text-[#9db3c8]">
            <CheckIcon aria-hidden className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-[#70d9ce]" />
            Administrator accounts do not count toward the participant allowance.
          </p>
        </div>
      </div>
    </Section>
  );
}
